import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useEffect, useState } from 'react';
import { MasterCropData } from '../../types';
import { useCropContextViewAllowed, useCropContextViewEnabled } from '../../hooks/useCropContextView';
import { CROP_CONTEXT_PADDING_PX, cropContextImageUrl } from '../../utils/cropContextImage';
import { getCropOrientationBadgeLabel } from './CropStatusBadge';

/** Beyond six panels nothing stays big enough to compare. */
export const CROP_COMPARE_MAX = 6;
/** Panels per row; more than three across leaves each one too narrow. */
const CROP_COMPARE_COLUMNS = 3;
export const CROP_COMPARE_MIN = 2;

export const CROP_COMPARE_TOO_MANY_MESSAGE = `Select at most ${CROP_COMPARE_MAX} crops to compare`;

/**
 * Zoom sizes the crop as a percentage of its panel's width, so 100% fills the panel
 * and 300% is three times that with the panel scrolling. It has to drive the size
 * rather than cap it: a crop is a few hundred pixels, so a maximum was never the
 * binding constraint and the slider appeared to do nothing.
 */
export const CROP_COMPARE_ZOOM_MIN = 60;
export const CROP_COMPARE_ZOOM_MAX = 300;
export const CROP_COMPARE_ZOOM_DEFAULT = 100;
const CROP_COMPARE_ZOOM_STEP = 10;
/** Keeps a tall crop from stretching the dialog; the panel scrolls instead. */
const CROP_COMPARE_VIEWPORT_MAX_HEIGHT = '68vh';
export const CROP_COMPARE_TOO_FEW_MESSAGE = `Select at least ${CROP_COMPARE_MIN} crops to compare`;

/**
 * One compared crop, rendered the way the grid renders it.
 *
 * A saved crop is a URL the image service already cropped. A Remote DC candidate is
 * a *draft*: the frontend holds the whole camera frame plus the detection's rect, so
 * it has to window the source image itself. Showing a draft's raw `imageUrl` puts an
 * uncropped frame (or a placeholder) next to real crops, which is what made
 * candidates look nothing like the crops beside them.
 */
const CompareCropImage: React.FC<{
  crop: MasterCropData;
  zoom: number;
  showContext: boolean;
}> = ({ crop, zoom, showContext }) => {
  const preview = crop.cropPreview;
  // `max-width: 100%` is set globally on img; without overriding it every zoom above
  // 100% is silently clamped back to the panel width.
  const unclamped = { maxWidth: 'none' } as const;

  if (!preview) {
    return (
      <img
        src={showContext ? cropContextImageUrl(crop.imageUrl) : crop.imageUrl}
        alt={`Crop ${crop.id}`}
        style={{ display: 'block', margin: '0 auto', width: `${zoom}%`, height: 'auto', ...unclamped }}
        draggable={false}
      />
    );
  }

  const padding = showContext ? CROP_CONTEXT_PADDING_PX : 0;
  const left = Math.max(0, preview.cropX - padding);
  const top = Math.max(0, preview.cropY - padding);
  const right = Math.min(preview.sourceWidth, preview.cropX + preview.cropWidth + padding);
  const bottom = Math.min(preview.sourceHeight, preview.cropY + preview.cropHeight + padding);
  const windowWidth = Math.max(1, right - left);
  const windowHeight = Math.max(1, bottom - top);
  const percentOf = (value: number, extent: number) => `${(value / extent) * 100}%`;

  return (
    <div
      data-testid={`crop-compare-draft-${crop.id}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        margin: '0 auto',
        width: `${zoom}%`,
        aspectRatio: `${windowWidth} / ${windowHeight}`,
      }}
    >
      <img
        src={preview.sourceImageUrl}
        alt={`Crop ${crop.id}`}
        style={{
          position: 'absolute',
          left: percentOf(-left, windowWidth),
          top: percentOf(-top, windowHeight),
          width: percentOf(preview.sourceWidth, windowWidth),
          height: percentOf(preview.sourceHeight, windowHeight),
          ...unclamped,
        }}
        draggable={false}
      />
      {padding > 0 && (
        // Matches the bounds the image service draws on a saved crop's wide view.
        <div
          style={{
            position: 'absolute',
            left: percentOf(preview.cropX - left, windowWidth),
            top: percentOf(preview.cropY - top, windowHeight),
            width: percentOf(preview.cropWidth, windowWidth),
            height: percentOf(preview.cropHeight, windowHeight),
            border: '2px solid #F97316',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  );
};

export interface CropComparePopupProps {
  crops: MasterCropData[];
  onClose: () => void;
}

interface CropDetail {
  label: string;
  value: string;
}

function formatTimestamp(value?: string): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function cropDetails(crop: MasterCropData): CropDetail[] {
  const details: CropDetail[] = [];
  const push = (label: string, value?: string | null) => {
    if (value) details.push({ label, value });
  };
  push('Item', crop.itemNumber);
  push('Barcode', crop.normalizedBarcode || crop.barcode || crop.normalizedUpc);
  push('Store', crop.storeId);
  push('Source', crop.forgeOriginSource || crop.source);
  push('Resolution', crop.imageResolution);
  push('Created', formatTimestamp(crop.createdAt));
  push('Created by', crop.createdBy);
  return details;
}

/**
 * Read-only side-by-side view of the selected crops. Deliberately has no editing
 * controls: it exists so labelers can tell near-identical designs apart before they
 * act on them in the grid behind it.
 */
export const CropComparePopup: React.FC<CropComparePopupProps> = ({ crops, onClose }) => {
  // Tight crops line the packaging up for comparison; context answers "where on the
  // shelf did this come from?" — both are worth a look, so the view is switchable
  // for the users who have context at all. It opens on whatever the grid is showing.
  const canShowContext = useCropContextViewAllowed();
  const gridShowsContext = useCropContextViewEnabled();
  const [showContext, setShowContext] = useState(gridShowsContext);
  // Three crops side by side are small; the slider trades panel height for detail
  // and the body scrolls once they outgrow the dialog.
  const [zoom, setZoom] = useState(CROP_COMPARE_ZOOM_DEFAULT);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      if (key === 'escape' || key === 'q') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [onClose]);

  const handleBackdropMouseDown = useCallback(
    (event: React.MouseEvent) => {
      if (event.target === event.currentTarget) onClose();
    },
    [onClose]
  );

  const visibleCrops = crops.slice(0, CROP_COMPARE_MAX);

  return (
    <div
      className="mdc-modal-backdrop"
      data-testid="crop-compare-backdrop"
      onMouseDown={handleBackdropMouseDown}
    >
      <div className="mdc-modal" role="dialog" aria-modal="true" aria-label="Compare crops">
        <div className="mdc-modal-header">
          <div>
            <strong>Compare crops</strong>
            <span style={{ marginLeft: 8, color: 'var(--text-tertiary)', fontSize: 12 }}>
              {visibleCrops.length} selected · read-only
            </span>
          </div>
          <div className="mdc-modal-header-actions">
            <span
              style={{
                fontSize: 11,
                fontFamily: 'var(--font-mono)',
                color: 'var(--text-tertiary)',
                whiteSpace: 'nowrap',
              }}
            >
              {zoom}%
            </span>
            <input
              aria-label="Compare crop zoom"
              data-testid="crop-compare-zoom"
              type="range"
              min={CROP_COMPARE_ZOOM_MIN}
              max={CROP_COMPARE_ZOOM_MAX}
              step={CROP_COMPARE_ZOOM_STEP}
              value={zoom}
              onChange={(event) => setZoom(Number(event.target.value))}
              onPointerUp={(event) => event.currentTarget.blur()}
              style={{ width: 90, accentColor: 'var(--accent)' }}
            />
            {canShowContext ? (
              <button
                type="button"
                className="dg-secondary-button"
                data-testid="crop-compare-context-toggle"
                aria-pressed={showContext}
                onClick={() => setShowContext((current) => !current)}
              >
                {showContext ? 'Hide wide view' : 'Show wide view'}
              </button>
            ) : null}
            <button
              type="button"
              className="dg-secondary-button"
              data-testid="crop-compare-close"
              aria-label="Close compare"
              onClick={onClose}
            >
              <CloseIcon style={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
        <div
          data-testid="crop-compare-body"
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${Math.min(
              Math.max(1, visibleCrops.length),
              CROP_COMPARE_COLUMNS
            )}, minmax(0, 1fr))`,
            gap: 12,
            padding: 12,
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          {visibleCrops.map((crop) => (
            <div
              key={crop.id}
              data-testid={`crop-compare-item-${crop.id}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                minWidth: 0,
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: 8,
                background: 'var(--surface-raised, var(--surface))',
              }}
            >
              <div
                data-testid={`crop-compare-viewport-${crop.id}`}
                style={{
                  maxHeight: CROP_COMPARE_VIEWPORT_MAX_HEIGHT,
                  overflow: 'auto',
                  borderRadius: 6,
                }}
              >
                <CompareCropImage
                  crop={crop}
                  zoom={zoom}
                  showContext={canShowContext && showContext}
                />
              </div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  fontSize: 11,
                  color: 'var(--text-secondary)',
                }}
              >
                {/* Same token the tile badge shows, so the two always agree. */}
                <span>Class {getCropOrientationBadgeLabel(crop.orientation)}</span>
                {crop.classificationType === 'carton' && <span>· Carton</span>}
                {crop.isInactive && <span>· Inactive</span>}
                {crop.isPending && <span>· Pending</span>}
              </div>
              <dl
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto minmax(0, 1fr)',
                  gap: '2px 8px',
                  margin: 0,
                  fontSize: 11,
                }}
              >
                {cropDetails(crop).map((detail) => (
                  <React.Fragment key={detail.label}>
                    <dt style={{ color: 'var(--text-tertiary)' }}>{detail.label}</dt>
                    <dd
                      style={{
                        margin: 0,
                        overflowWrap: 'anywhere',
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {detail.value}
                    </dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
